import { validateHex } from './colors';
import { extractParam } from './queryParams';

/**
 * Parse theme + color config from the query params (P3-7 / MR-35).
 *
 * Extracted verbatim from app.vue's `processQueryParams`: the
 * `v3PlayerConfigs.theme` switch, the `v3PlayerConfigs.preferences` block, and
 * the `v4PlayerConfigs` color/vertical assignments. app.vue merges the returned
 * pieces into its config objects. Every value is byte-identical to the original
 * (same `validateHex`/`extractParam` calls, same dark-preset hexes, same XSS
 * lock: an invalid hex → `null`, which the v3 light path defaults away).
 *
 * The dark presets (#222046 / #ffffff / #532aa2) and the v3 light defaults
 * (#ffffff / #222046 / #532aa2) are preserved. `vertical` and each preference
 * are true only for the literal string 'true' (`galleryLimit` is `limit || 0`,
 * raw — not parsed).
 *
 * @param {Object<string,string>} queryParams parsed query params
 * @returns {{isDarkTheme:boolean, v3Theme:Object, v3Preferences:Object,
 *   v4Colors:Object, vertical:boolean}}
 */
export const parseTheme = (queryParams) => {
  const type = queryParams.theme || 'light';
  const isDarkTheme = type === 'dark';

  const v3Theme = { type };
  switch (type) {
    case 'dark':
      v3Theme.background = '#222046';
      v3Theme.text = '#ffffff';
      v3Theme.accent = '#532aa2';
      break;

    default:
      // light theme shouldn't have background/text/accent params, so they'll fallback to default
      v3Theme.background = validateHex(queryParams.background) || '#ffffff';
      v3Theme.text = validateHex(queryParams.text) || '#222046';
      v3Theme.accent = validateHex(queryParams.accent) || '#532aa2';
  }

  const v3Preferences = {
    enableAlbumArt: queryParams.albumart === 'true' || false,
    enableHashtags: queryParams.hashtags === 'true' || false,
    enableShowNotes: queryParams.shownotes === 'true' || false,
    enableGallery: queryParams.gallery === 'true' || false,
    galleryLimit: queryParams.limit || 0,
  };

  const v4Colors = {
    background: validateHex(extractParam(queryParams, 'background')),
    play_button: validateHex(extractParam(queryParams, 'play_button')),
    start_remaining: validateHex(extractParam(queryParams, 'start_remaining')),
  };

  const vertical = queryParams.vertical === 'true';

  return {
    isDarkTheme, v3Theme, v3Preferences, v4Colors, vertical,
  };
};
